import { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw, Building2 } from 'lucide-react';
import * as apiClient from '@/services/apiClient';
import CompaniesTableSection from '@/components/superadmin/CompaniesTableSection';
import CreateCompanyWizard, { CredentialsBanner } from '@/components/superadmin/CreateCompanyWizard';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 10;

export default function SuperAdminCompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [credentials, setCredentials] = useState(null);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.getAllCompanies();
      setCompanies(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(err.message || 'Failed to load companies.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const handleCreated = (payload) => {
    setCredentials(payload);
    fetchCompanies();
  };

  if (loading && companies.length === 0) {
    return (
      <div className="mx-auto  space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 rounded-md" />
      </div>
    );
  }

  return (
    <div className="mx-auto  space-y-4 pb-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Platform</p>
          <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
            <Building2 className="h-5 w-5 text-primary" />
            Companies
          </h1>
          <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">Manage all tenant organizations</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={fetchCompanies} disabled={loading}>
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
            Refresh
          </Button>
          <Button size="sm" className="h-9 gap-1.5 shadow-sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            New company
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <span>{error}</span>
          <button type="button" className="text-xs font-medium underline" onClick={() => setError('')}>Dismiss</button>
        </div>
      )}

      <CredentialsBanner credentials={credentials} onDismiss={() => setCredentials(null)} />

      <CompaniesTableSection
        companies={companies}
        pageSize={PAGE_SIZE}
        title="All companies"
      />

      <CreateCompanyWizard open={createOpen} onOpenChange={setCreateOpen} onCreated={handleCreated} />
    </div>
  );
}
